import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Button, Paper, Typography} from '@mui/material';
import {useTutorial} from '@/context/TutorialContext';
import {getNavTarget, type TutorialStep} from '@/lib/tutorial/tutorialSteps';
import usePlatform from '@/hooks/usePlatform';
import type {TFunction} from 'i18next';

type Rect = {top: number; left: number; width: number; height: number};

/** Padding (in px) around the spotlighted element inside the dark overlay cutout. */
const TARGET_PADDING = 1;
/** Distance (in px) between the popover card and the spotlight cutout edge. */
const POPOVER_GAP = 12;
const POLL_INTERVAL_MS = 100;
// Subpages lazy-load behind Suspense, so the target (e.g. the AG Grid root) can mount later
// than 2s after a tour-driven navigation. Give late-mounting targets more time to appear.
const POLL_TIMEOUT_MS = 4000;

function findTargetElement(selector: string | (() => HTMLElement | null)): HTMLElement | null {
    if (typeof selector === 'function') return selector();
    return document.querySelector(selector) as HTMLElement | null;
}

function rectOf(el: HTMLElement): Rect {
    const r = el.getBoundingClientRect();
    return {top: r.top, left: r.left, width: r.width, height: r.height};
}

function rectsEqual(a: Rect | null, b: Rect): boolean {
    return !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

const popoverSx = {
    position: 'absolute',
    p: 2.5,
    borderRadius: 2,
    border: '2px solid',
    borderColor: 'primary.main',
    boxShadow: 6,
    bgcolor: 'background.paper',
    width: '100%',
    maxWidth: {xs: 'calc(100vw - 2rem)', sm: 320},
} as const;

type PopoverBodyProps = {
    step: TutorialStep;
    stepLabel: string;
    isFirst: boolean;
    isLast: boolean;
    onNext: () => void;
    onBack: () => void;
    onSkip: () => void;
    t: TFunction;
};

const PopoverBody: React.FC<PopoverBodyProps> = ({step, stepLabel, isFirst, isLast, onNext, onBack, onSkip, t}) => (
    <>
        <Typography
            variant="caption"
            sx={{display: 'block', textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', fontWeight: 600, mb: 0.5}}
        >
            {stepLabel}
        </Typography>
        <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1}}>
            {step.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            {step.content}
        </Typography>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
            {isLast ? <span/> : (
                <Button size="small" color="inherit" onClick={onSkip}>
                    {t('tutorial.ui.skip')}
                </Button>
            )}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                {!isFirst && (
                    <Button size="small" color="inherit" onClick={onBack}>
                        {t('tutorial.ui.back')}
                    </Button>
                )}
                <Button size="small" variant="contained" onClick={onNext}>
                    {isLast ? t('tutorial.ui.done') : t('tutorial.ui.next')}
                </Button>
            </Box>
        </Box>
    </>
);

export const SpotlightOverlay: React.FC = () => {
    const {
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps,
        isOnCorrectPage,
        next,
        back,
        skip,
    } = useTutorial();
    const {t} = useTranslation();
    const platform = usePlatform();

    const [targetRect, setTargetRect] = useState<Rect | null>(null);

    const setTargetRectIfChanged = useCallback((next: Rect) => {
        setTargetRect((prev) => (rectsEqual(prev, next) ? prev : next));
    }, []);

    useEffect(() => {
        const shouldLock = isActive && isOnCorrectPage && !currentStep?.targetSelector;
        const prev = document.body.style.overflow;
        if (shouldLock) document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isActive, isOnCorrectPage, currentStep?.targetSelector]);

    // Track the on-page target element via ResizeObserver + scroll/resize, with rAF coalescing.
    useEffect(() => {
        setTargetRect(null);
        if (!isActive || !isOnCorrectPage || !currentStep?.targetSelector) return;
        const selector = currentStep.targetSelector;

        let observer: ResizeObserver | null = null;
        let pollInterval: ReturnType<typeof setInterval> | null = null;
        let pollTimeout: ReturnType<typeof setTimeout> | null = null;
        let rafId = 0;
        let attachedEl: HTMLElement | null = null;

        const measure = () => {
            if (attachedEl) setTargetRectIfChanged(rectOf(attachedEl));
        };
        const scheduleMeasure = () => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                measure();
            });
        };

        const attach = (): boolean => {
            const el = findTargetElement(selector);
            if (!el) return false;
            attachedEl = el;
            setTargetRectIfChanged(rectOf(el));
            el.scrollIntoView({behavior: 'smooth', block: 'center'});
            observer = new ResizeObserver(scheduleMeasure);
            observer.observe(el);
            window.addEventListener('scroll', scheduleMeasure, {passive: true, capture: true});
            window.addEventListener('resize', scheduleMeasure, {passive: true});
            return true;
        };

        if (!attach()) {
            pollInterval = setInterval(() => {
                if (attach() && pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                    if (pollTimeout) {
                        clearTimeout(pollTimeout);
                        pollTimeout = null;
                    }
                }
            }, POLL_INTERVAL_MS);
            pollTimeout = setTimeout(() => {
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            }, POLL_TIMEOUT_MS);
        }

        return () => {
            observer?.disconnect();
            if (pollInterval) clearInterval(pollInterval);
            if (pollTimeout) clearTimeout(pollTimeout);
            if (rafId) cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', scheduleMeasure, true);
            window.removeEventListener('resize', scheduleMeasure);
        };
    }, [isActive, isOnCorrectPage, currentStep?.targetSelector, currentStepIndex, setTargetRectIfChanged]);

    if (!isActive || !currentStep) return null;

    const hasSpotlight = !!currentStep.targetSelector && !!targetRect;
    const isFirst = currentStepIndex === 0;
    const isLast = currentStepIndex === totalSteps - 1;
    const stepLabel = t('tutorial.ui.step', {current: currentStepIndex + 1, total: totalSteps});
    const popoverProps = {step: currentStep, stepLabel, isFirst, isLast, onNext: next, onBack: back, onSkip: skip, t};

    // Off the current step's page: the tour drives the router automatically (in both modes).
    // Show a small, non-blocking transitional card so the user sees the tour is moving.
    if (!isOnCorrectPage) {
        const navTarget = getNavTarget(t, currentStep, platform);
        return (
            <Box sx={{position: 'fixed', inset: 0, zIndex: 1300, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2}}>
                <Paper
                    sx={{...popoverSx, position: 'relative', pointerEvents: 'auto'}}
                    elevation={8}
                >
                    <Typography
                        variant="caption"
                        sx={{display: 'block', textTransform: 'uppercase', letterSpacing: 1, color: 'text.secondary', fontWeight: 600, mb: 0.5}}
                    >
                        {stepLabel}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={600} sx={{mb: 0.5}}>
                        {currentStep.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t('tutorial.ui.opening', {label: navTarget.label})}
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                        <Button size="small" color="inherit" onClick={skip}>
                            {t('tutorial.ui.skip')}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    if (currentStep.nonBlocking && !hasSpotlight) {
        return (
            <Box sx={{position: 'fixed', inset: 0, zIndex: 1300, pointerEvents: 'none'}}>
                <Box sx={{position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.15)'}}/>
                <Paper
                    sx={{...popoverSx, pointerEvents: 'auto', left: 0, right: 0, mx: 'auto', bottom: 24}}
                    elevation={6}
                >
                    <PopoverBody {...popoverProps}/>
                </Paper>
            </Box>
        );
    }

    if (!hasSpotlight) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1300,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                }}
            >
                <Paper sx={{...popoverSx, position: 'relative'}} elevation={8}>
                    <PopoverBody {...popoverProps}/>
                </Paper>
            </Box>
        );
    }

    const {top, left, width, height} = targetRect!;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = left - TARGET_PADDING;
    const cy = top - TARGET_PADDING;
    const cw = width + TARGET_PADDING * 2;
    const ch = height + TARGET_PADDING * 2;

    const popoverMaxW = Math.min(vw - 32, 320);
    const spaceBelow = vh - (top + height + TARGET_PADDING);
    const spaceAbove = top - TARGET_PADDING;
    const popoverLeft = Math.max(8, Math.min(left, vw - popoverMaxW - 8));
    const centeredLeft = Math.max(8, Math.min((vw - popoverMaxW) / 2, vw - popoverMaxW - 8));
    const CARD_H = 260;
    const isTargetVertical = height > vh * 0.6 && width < vw * 0.4;

    const verticalCenterTop = Math.max(8, Math.min(top + height / 2 - CARD_H / 2, vh - CARD_H - 8));
    const horizontalCenterLeft = Math.max(8, Math.min(left + width / 2 - popoverMaxW / 2, vw - popoverMaxW - 8));

    let popoverStyle: React.CSSProperties;
    if (currentStep.placement === 'pinned-bottom') {
        popoverStyle = {bottom: 24, left: centeredLeft};
    } else if (currentStep.placement === 'right') {
        popoverStyle = {top: verticalCenterTop, left: left + width + TARGET_PADDING + POPOVER_GAP};
    } else if (currentStep.placement === 'left') {
        popoverStyle = {top: verticalCenterTop, right: vw - left + TARGET_PADDING + POPOVER_GAP};
    } else if (currentStep.placement === 'top') {
        popoverStyle = {bottom: vh - top + TARGET_PADDING + POPOVER_GAP, left: horizontalCenterLeft};
    } else if (currentStep.placement === 'bottom') {
        popoverStyle = {top: top + height + TARGET_PADDING + POPOVER_GAP, left: horizontalCenterLeft};
    } else if (isTargetVertical) {
        const verticalTop = Math.max(8, Math.min(72, vh - CARD_H - 8));
        const targetIsOnRight = left > vw / 2;
        popoverStyle = targetIsOnRight
            ? {top: verticalTop, right: vw - left + TARGET_PADDING + POPOVER_GAP}
            : {top: verticalTop, left: left + width + TARGET_PADDING + POPOVER_GAP};
    } else if (spaceBelow >= CARD_H) {
        popoverStyle = {top: top + height + TARGET_PADDING + POPOVER_GAP, left: popoverLeft};
    } else if (spaceAbove >= CARD_H) {
        popoverStyle = {bottom: vh - (top - TARGET_PADDING - POPOVER_GAP), left: popoverLeft};
    } else if (spaceAbove >= spaceBelow) {
        popoverStyle = {bottom: Math.min(vh - (top - TARGET_PADDING - POPOVER_GAP), vh - CARD_H), left: popoverLeft};
    } else {
        popoverStyle = {top: Math.min(top + height + TARGET_PADDING + POPOVER_GAP, vh - CARD_H), left: popoverLeft};
    }

    // Subtle dim for non-blocking steps so the page stays interactive but the spotlight pops.
    const dimFill = currentStep.nonBlocking ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.7)';

    return (
        <Box sx={{position: 'fixed', inset: 0, zIndex: 1300, pointerEvents: 'none'}}>
            <svg width={vw} height={vh} style={{position: 'absolute', inset: 0, display: 'block'}}>
                <defs>
                    <mask id="tutorial-spotlight-mask">
                        <rect width={vw} height={vh} fill="white"/>
                        <rect x={cx} y={cy} width={cw} height={ch} rx={8} ry={8} fill="black"/>
                    </mask>
                </defs>
                <rect width={vw} height={vh} fill={dimFill} mask="url(#tutorial-spotlight-mask)"/>
                <rect x={cx} y={cy} width={cw} height={ch} rx={8} ry={8} fill="none" stroke="#1976d2" strokeWidth={2}/>
            </svg>
            <Paper
                sx={{...popoverSx, pointerEvents: 'auto'}}
                style={{...popoverStyle, width: popoverMaxW}}
                elevation={6}
            >
                <PopoverBody {...popoverProps}/>
            </Paper>
        </Box>
    );
};

export default SpotlightOverlay;
